import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getEnrichedEnquiries, updateEnquiry, addEnquiryNote, updateEnquiryTags,
  updateEnquiryFollowup, updateEnquiryTemperature, createViewing, completeViewing,
  createReservation, confirmReservation, createEnquiry,
  savePersonalDetails, uploadOnboardingDoc, completeOnboarding, sendContract, signContract,
  reviewOnboarding, autoExpireReservations,
  sendApplicationLink, sendLicence, markLicenceSigned,
  requestPaymentAction, confirmPaymentAction, issueAccess
} from '@/lib/api';
import {
  ClipboardList, Loader2, Mail, Phone, ChevronDown, ChevronUp, ChevronRight,
  ArrowRight, MessageSquare, Tag, MapPin, CalendarClock, Send, X, Check,
  Thermometer, Clock, AlertCircle, DoorOpen, Calendar, Eye, Plus,
  CreditCard, Key, FileText
} from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';

const STAGES = [
  { key: 'new', label: 'New', color: 'bg-[#F4EFE6]', text: 'text-[#7A6849]', dot: 'bg-[#C4A44E]', action: 'Contact', nextStatus: 'contacted' },
  { key: 'contacted', label: 'Contacted', color: 'bg-[#E8EDE5]', text: 'text-[#3A4035]', dot: 'bg-[#6B7A60]', action: 'Book Viewing', nextStatus: null },
  { key: 'viewing_booked', label: 'Viewing', color: 'bg-[#E0E6DE]', text: 'text-[#3A4035]', dot: 'bg-[#4A5E42]', action: 'Complete Viewing', nextStatus: null },
  { key: 'reserved', label: 'Reserved', color: 'bg-[#3A4035]', text: 'text-white', dot: 'bg-[#3A4035]', action: null, nextStatus: null },
  { key: 'application_sent', label: 'Applied', color: 'bg-[#3A4035]', text: 'text-white', dot: 'bg-[#3A4035]', action: null, nextStatus: null },
  { key: 'application_submitted', label: 'Submitted', color: 'bg-[#3A4035]', text: 'text-white', dot: 'bg-[#3A4035]', action: null, nextStatus: null },
  { key: 'licence_sent', label: 'Licence Sent', color: 'bg-[#2B3027]', text: 'text-white', dot: 'bg-[#2B3027]', action: null, nextStatus: null },
  { key: 'licence_signed', label: 'Signed', color: 'bg-[#2B3027]', text: 'text-white', dot: 'bg-[#2B3027]', action: null, nextStatus: null },
  { key: 'payment_pending', label: 'Payment', color: 'bg-[#2B3027]', text: 'text-white', dot: 'bg-[#2B3027]', action: null, nextStatus: null },
  { key: 'payment_confirmed', label: 'Paid', color: 'bg-[#2B3027]', text: 'text-white', dot: 'bg-[#2B3027]', action: null, nextStatus: null },
  { key: 'active', label: 'Active', color: 'bg-[#1A1A1A]', text: 'text-white', dot: 'bg-[#1A1A1A]', action: null, nextStatus: null },
];
const SUGGESTED_TAGS = ['hot_lead', 'budget_sensitive', 'location_mismatch', 'waiting_list', 'returning_client', 'referral', 'matching_opportunity'];
const tagColors = { hot_lead: 'bg-[#FDF2F2] text-[#B33A3A] border-[#F0D4D4]', budget_sensitive: 'bg-[#FDFAF0] text-[#8A7230] border-[#EDE4C8]', location_mismatch: 'bg-[#F2F0EB] text-[#5C5C55] border-[#E5E2DC]', waiting_list: 'bg-[#F0F2EF] text-[#3A4035] border-[#D8DBD3]', returning_client: 'bg-[#E8EDE5] text-[#3A4035] border-[#D0D9CB]', referral: 'bg-[#E8EDE5] text-[#3A4035] border-[#D0D9CB]', matching_opportunity: 'bg-[#EDE8F5] text-[#5B4A8A] border-[#D8D0E8]' };
const tempConfig = { cold: { label: 'Cold', bg: 'bg-[#E8EDF2]', text: 'text-[#4A6080]', border: 'border-[#D0DAE5]', dot: 'bg-[#4A6080]' }, warm: { label: 'Warm', bg: 'bg-[#FDFAF0]', text: 'text-[#8A7230]', border: 'border-[#EDE4C8]', dot: 'bg-[#C4A44E]' }, hot: { label: 'Hot', bg: 'bg-[#FDF2F2]', text: 'text-[#B33A3A]', border: 'border-[#F0D4D4]', dot: 'bg-[#B33A3A]' } };

function timeAgo(iso) { if (!iso) return 'Never'; const d = Math.floor((Date.now() - new Date(iso).getTime()) / 1000); if (d < 60) return 'Just now'; if (d < 3600) return `${Math.floor(d/60)}m`; if (d < 86400) return `${Math.floor(d/3600)}h`; const days = Math.floor(d/86400); return days === 1 ? '1d' : days < 30 ? `${days}d` : new Date(iso).toLocaleDateString('en-GB',{day:'numeric',month:'short'}); }
function daysSince(iso) { if (!iso) return Infinity; return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000); }
function formatDT(iso) { if (!iso) return ''; const d = new Date(iso); return d.toLocaleDateString('en-GB',{day:'numeric',month:'short'}) + ' ' + d.toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'}); }
function isOverdue(enq) { return enq.follow_up_required && enq.follow_up_date && enq.follow_up_date < new Date().toISOString().split('T')[0]; }

// ── Book Viewing Modal ──
function BookViewingModal({ open, onClose, enq, sites, studios, onBooked }) {
  const [date, setDate] = useState('');
  const [time, setTime] = useState('10:00');
  const [siteId, setSiteId] = useState('');
  const [studioId, setStudioId] = useState('');
  const [saving, setSaving] = useState(false);
  const availableStudios = studios.filter(s => s.site_id === siteId && (s.status === 'vacant' || s.status === 'reserved'));

  const handleSave = async () => {
    if (!date) return;
    setSaving(true);
    try {
      await createViewing({ enquiry_id: enq.enquiry_id, scheduled_date: date, scheduled_time: time, site_id: siteId, studio_id: studioId, notes: '' });
      onBooked();
      onClose();
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-white rounded-2xl border-[#E5E2DC]" data-testid="book-viewing-modal">
        <DialogHeader><DialogTitle className="font-heading text-lg">Book Viewing — {enq.name}</DialogTitle><DialogDescription className="sr-only">Schedule a viewing</DialogDescription></DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55] block mb-1.5">Date</label>
              <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="bg-[#F2F0EB] border-transparent rounded-xl h-10" data-testid="viewing-date" />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55] block mb-1.5">Time</label>
              <Input type="time" value={time} onChange={e => setTime(e.target.value)} className="bg-[#F2F0EB] border-transparent rounded-xl h-10" data-testid="viewing-time" />
            </div>
          </div>
          <div>
            <label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55] block mb-1.5">Site</label>
            <Select value={siteId} onValueChange={v => { setSiteId(v); setStudioId(''); }}>
              <SelectTrigger className="bg-[#F2F0EB] border-transparent rounded-xl h-10" data-testid="viewing-site"><SelectValue placeholder="Select site" /></SelectTrigger>
              <SelectContent>{sites.map(s => <SelectItem key={s.site_id} value={s.site_id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {siteId && availableStudios.length > 0 && (
            <div>
              <label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55] block mb-1.5">Studio (optional)</label>
              <Select value={studioId} onValueChange={setStudioId}>
                <SelectTrigger className="bg-[#F2F0EB] border-transparent rounded-xl h-10" data-testid="viewing-studio"><SelectValue placeholder="Any available" /></SelectTrigger>
                <SelectContent>{availableStudios.map(s => <SelectItem key={s.studio_id} value={s.studio_id}>{s.name} — £{s.monthly_rent}/mo</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <Button onClick={handleSave} disabled={saving || !date} className="w-full bg-[#3A4035] text-white rounded-full h-10 hover:bg-[#2B3027]" data-testid="confirm-viewing-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Calendar className="w-4 h-4 mr-2" strokeWidth={1.5} />}Book Viewing
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Viewing Outcome Modal (with insight capture) ──
function ViewingOutcomeModal({ open, onClose, enq, onComplete, onReserveAfter }) {
  const [saving, setSaving] = useState(false);
  const [interestLevel, setInterestLevel] = useState('');
  const [hesitation, setHesitation] = useState('');
  const [opNotes, setOpNotes] = useState('');
  const scheduledViewing = (enq.viewings || []).find(v => v.status === 'scheduled');

  const handleOutcome = async (o) => {
    if (!scheduledViewing) return;
    setSaving(true);
    try {
      await completeViewing(scheduledViewing.viewing_id, o, {
        interest_level: interestLevel, hesitation, operator_notes: opNotes,
      });
      if (o === 'interested') {
        onComplete(o);
        onClose();
        setTimeout(() => onReserveAfter(enq), 300);
      } else {
        onComplete(o);
        onClose();
      }
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  // Reset on open
  React.useEffect(() => { if (open) { setInterestLevel(''); setHesitation(''); setOpNotes(''); } }, [open]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-white rounded-2xl border-[#E5E2DC]" data-testid="viewing-outcome-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">Viewing Outcome — {enq.name}</DialogTitle>
          <DialogDescription className="text-xs text-[#5C5C55]">Quick capture before recording the outcome</DialogDescription>
        </DialogHeader>

        {/* Quick insight fields */}
        <div className="space-y-3 pt-1 pb-2">
          {/* Interest level */}
          <div>
            <p className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#8A8B85] mb-1.5">Interest Level</p>
            <div className="flex gap-2">
              {[
                { key: 'high', label: 'High', color: 'bg-[#E8EDE5] text-[#3A4035] border-[#D0D9CB]' },
                { key: 'medium', label: 'Medium', color: 'bg-[#FDFAF0] text-[#8A7230] border-[#EDE4C8]' },
                { key: 'low', label: 'Low', color: 'bg-[#F2F0EB] text-[#5C5C55] border-[#E5E2DC]' },
              ].map(lvl => (
                <button key={lvl.key} onClick={() => setInterestLevel(lvl.key)}
                  className={`flex-1 text-xs py-2 rounded-xl font-medium border transition-all ${interestLevel === lvl.key ? lvl.color : 'bg-white text-[#8A8B85] border-[#E5E2DC] hover:border-[#D8DBD3]'}`}
                  data-testid={`interest-${lvl.key}`}>
                  {lvl.label}
                </button>
              ))}
            </div>
          </div>
          {/* Hesitation */}
          <div>
            <p className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#8A8B85] mb-1.5">Main Hesitation</p>
            <div className="flex flex-wrap gap-1.5">
              {['Price', 'Location', 'Size', 'Not ready', 'Comparing', 'None', 'Other'].map(h => (
                <button key={h} onClick={() => setHesitation(h === hesitation ? '' : h)}
                  className={`text-[11px] px-3 py-1.5 rounded-full font-medium border transition-all ${hesitation === h ? 'bg-[#3A4035] text-white border-[#3A4035]' : 'bg-white text-[#8A8B85] border-[#E5E2DC] hover:border-[#D8DBD3]'}`}
                  data-testid={`hesitation-${h.toLowerCase().replace(' ', '-')}`}>
                  {h}
                </button>
              ))}
            </div>
          </div>
          {/* Operator notes */}
          <div>
            <p className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#8A8B85] mb-1.5">Quick Notes</p>
            <Textarea value={opNotes} onChange={e => setOpNotes(e.target.value)} placeholder="Anything to capture..." rows={2}
              className="bg-[#F2F0EB] border-transparent rounded-xl text-xs placeholder:text-[#8A8B85] resize-none" data-testid="viewing-notes" />
          </div>
        </div>

        {/* Divider + nudge */}
        <div className="border-t border-[#F2F0EB]" />
        {!interestLevel && !hesitation && !opNotes.trim() && (
          <p className="text-[11px] text-[#8A8B85] text-center -mb-1" data-testid="insight-nudge">A quick note helps improve future viewings</p>
        )}

        {/* Outcome buttons */}
        <div className="space-y-2 pt-1">
          <button onClick={() => handleOutcome('interested')} disabled={saving} className="w-full flex items-center gap-3 p-4 rounded-2xl border border-[#D0D9CB] bg-[#F0F2EF] hover:bg-[#E8EDE5] transition-all text-left" data-testid="outcome-interested">
            <div className="w-8 h-8 rounded-xl bg-[#3A4035] flex items-center justify-center"><DoorOpen className="w-4 h-4 text-white" strokeWidth={1.5} /></div>
            <div className="flex-1"><p className="text-sm font-medium text-[#1A1A1A]">Interested — Reserve studio</p><p className="text-[11px] text-[#5C5C55]">Secure the space while they decide</p></div>
            <ArrowRight className="w-4 h-4 text-[#3A4035]" strokeWidth={1.5} />
          </button>
          <button onClick={() => handleOutcome('needs_time')} disabled={saving} className="w-full flex items-center gap-3 p-4 rounded-2xl border border-[#E5E2DC] hover:bg-[#FDFAF0] hover:border-[#EDE4C8] transition-all text-left" data-testid="outcome-needs-time">
            <div className="w-8 h-8 rounded-xl bg-[#FDFAF0] flex items-center justify-center"><CalendarClock className="w-4 h-4 text-[#8A7230]" strokeWidth={1.5} /></div>
            <div><p className="text-sm font-medium text-[#1A1A1A]">Needs time to decide</p><p className="text-[11px] text-[#8A8B85]">Auto-set 48h follow-up</p></div>
          </button>
          <button onClick={() => handleOutcome('not_interested')} disabled={saving} className="w-full flex items-center gap-3 p-4 rounded-2xl border border-[#E5E2DC] hover:bg-[#FDF2F2] hover:border-[#F0D4D4] transition-all text-left" data-testid="outcome-not-interested">
            <div className="w-8 h-8 rounded-xl bg-[#FDF2F2] flex items-center justify-center"><X className="w-4 h-4 text-[#B33A3A]" strokeWidth={1.5} /></div>
            <div><p className="text-sm font-medium text-[#1A1A1A]">Not interested</p><p className="text-[11px] text-[#8A8B85]">Mark lead as cold</p></div>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Reserve Studio Modal ──
function ReserveStudioModal({ open, onClose, enq, studios, sites, onReserved }) {
  const [studioId, setStudioId] = useState('');
  const [expiry, setExpiry] = useState(() => { const d = new Date(); d.setDate(d.getDate() + 5); return d.toISOString().split('T')[0]; });
  const [saving, setSaving] = useState(false);
  const siteMap = {};
  sites.forEach(s => { siteMap[s.site_id] = s.name; });
  const vacant = studios.filter(s => s.status === 'vacant');

  const handleReserve = async () => {
    if (!studioId) return;
    setSaving(true);
    try {
      await createReservation({ enquiry_id: enq.enquiry_id, studio_id: studioId, expiry_date: expiry });
      onReserved();
      onClose();
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-white rounded-2xl border-[#E5E2DC]" data-testid="reserve-studio-modal">
        <DialogHeader><DialogTitle className="font-heading text-lg">Reserve Studio — {enq.name}</DialogTitle><DialogDescription className="sr-only">Reserve a studio</DialogDescription></DialogHeader>

        {/* Reassurance */}
        <div className="bg-[#F0F2EF] rounded-xl p-4 border border-[#D8DBD3] -mt-1" data-testid="reserve-reassurance">
          <p className="text-sm text-[#3A4035] leading-relaxed">Reserving a studio simply secures the space while you decide — no long-term commitment at this stage.</p>
        </div>

        <div className="space-y-4 pt-1">
          <div>
            <label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55] block mb-1.5">Select Studio</label>
            <Select value={studioId} onValueChange={setStudioId}>
              <SelectTrigger className="bg-[#F2F0EB] border-transparent rounded-xl h-10" data-testid="reserve-studio-select"><SelectValue placeholder="Choose a studio" /></SelectTrigger>
              <SelectContent>{vacant.map(s => <SelectItem key={s.studio_id} value={s.studio_id}>{s.name} — {siteMap[s.site_id] || ''} — £{s.monthly_rent}/mo</SelectItem>)}</SelectContent>
            </Select>
            {/* Subtle scarcity */}
            <p className="text-[10px] text-[#8A8B85] mt-1.5">Studios at this site are limited — each space is one of one.</p>
          </div>
          <div>
            <label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55] block mb-1.5">Reservation Expiry</label>
            <Input type="date" value={expiry} onChange={e => setExpiry(e.target.value)} className="bg-[#F2F0EB] border-transparent rounded-xl h-10" data-testid="reserve-expiry" />
          </div>
          <Button onClick={handleReserve} disabled={saving || !studioId} className="w-full bg-[#3A4035] text-white rounded-full h-10 hover:bg-[#2B3027]" data-testid="confirm-reserve-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <DoorOpen className="w-4 h-4 mr-2" strokeWidth={1.5} />}Reserve Studio
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Enquiry Card ──

const WORKFLOW_STEPS = [
  { key: 'reserved', label: 'Reserved' },
  { key: 'application', label: 'Application' },
  { key: 'review', label: 'Review' },
  { key: 'licence', label: 'Licence' },
  { key: 'payment', label: 'Payment' },
  { key: 'access', label: 'Access' },
  { key: 'active', label: 'Active' },
];

function getWorkflowStep(enqStatus, reservation) {
  const s = enqStatus || '';
  const appStatus = reservation?.application_status || 'not_sent';
  const onbStatus = reservation?.onboarding_status || 'not_started';
  const licStatus = reservation?.licence_status || 'not_sent';
  const payStatus = reservation?.payment_status || 'not_requested';
  const accStatus = reservation?.access_status || 'not_issued';

  if (accStatus === 'issued' || s === 'active') return 6;
  if (payStatus === 'payment_confirmed') return 5;
  if (payStatus === 'payment_pending') return 4;
  if (licStatus === 'signed' || s === 'licence_signed') return 4;
  if (licStatus === 'sent_for_signature' || s === 'licence_sent') return 3;
  if (onbStatus === 'approved') return 2;
  if (onbStatus === 'under_review' || s === 'application_submitted') return 2;
  if (appStatus === 'sent' || appStatus === 'in_progress' || s === 'application_sent') return 1;
  return 0;
}

function WorkflowProgress({ reservation, enquiryStatus, contract }) {
  const current = getWorkflowStep(enquiryStatus, reservation);
  return (
    <div className="flex items-center gap-0.5 overflow-x-auto" data-testid="workflow-progress">
      {WORKFLOW_STEPS.map((step, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <React.Fragment key={step.key}>
            {i > 0 && <div className={`w-3 h-px shrink-0 ${done ? 'bg-[#3A4035]' : 'bg-[#E5E2DC]'}`} />}
            <div className={`flex items-center gap-1 shrink-0 px-1.5 py-1 rounded-lg ${active ? 'bg-[#E8EDE5]' : ''}`}>
              <div className={`w-4 h-4 rounded-full flex items-center justify-center text-[7px] font-bold ${done ? 'bg-[#3A4035] text-white' : active ? 'bg-[#3A4035] text-white' : 'bg-[#F2F0EB] text-[#8A8B85]'}`}>
                {done ? <Check className="w-2.5 h-2.5" strokeWidth={2.5} /> : i + 1}
              </div>
              <span className={`text-[9px] font-medium ${done || active ? 'text-[#3A4035]' : 'text-[#8A8B85]'}`}>{step.label}</span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function WorkflowAction({ enq, reservation, onAction }) {
  const s = enq.status || '';
  const appStatus = reservation?.application_status || 'not_sent';
  const onbStatus = reservation?.onboarding_status || 'not_started';
  const licStatus = reservation?.licence_status || 'not_sent';
  const payStatus = reservation?.payment_status || 'not_requested';
  const accStatus = reservation?.access_status || 'not_issued';

  // Active — done
  if (accStatus === 'issued' || s === 'active') {
    return <div className="flex items-center gap-2 p-3 bg-[#E8EDE5] rounded-xl"><Check className="w-4 h-4 text-[#3A4035]" strokeWidth={1.5} /><p className="text-xs font-medium text-[#3A4035]">Occupier active — onboarding complete</p></div>;
  }

  // Step 1: Send application
  if (s === 'reserved' && ['not_sent'].includes(appStatus)) {
    return <button onClick={() => onAction('send_application', enq)} className="w-full flex items-center gap-3 p-3 rounded-xl bg-[#3A4035] text-white hover:bg-[#2B3027] transition-colors" data-testid="wf-send-application"><Send className="w-4 h-4 shrink-0" strokeWidth={1.5} /><div className="text-left"><p className="text-xs font-medium">Send Application Link</p><p className="text-[10px] text-white/60">Applicant will complete form and upload documents</p></div></button>;
  }

  // Waiting for applicant
  if (['application_sent'].includes(s) && ['sent', 'in_progress'].includes(appStatus) && !['under_review', 'approved'].includes(onbStatus)) {
    return <div className="flex items-center gap-2 p-3 bg-[#FDFAF0] rounded-xl border border-[#EDE4C8]"><CalendarClock className="w-4 h-4 text-[#8A7230] shrink-0" strokeWidth={1.5} /><p className="text-xs text-[#8A7230]">Waiting for applicant to complete application</p></div>;
  }

  // Step 2: Review application
  if (onbStatus === 'under_review') {
    return <button onClick={() => onAction('review', enq)} className="w-full flex items-center gap-3 p-3 rounded-xl bg-[#FDFAF0] border border-[#EDE4C8] hover:bg-[#F8F0D8] transition-colors text-left" data-testid="wf-review"><Eye className="w-4 h-4 text-[#8A7230] shrink-0" strokeWidth={1.5} /><div><p className="text-xs font-medium text-[#8A7230]">Review Application</p><p className="text-[10px] text-[#8A7230]/60">Documents uploaded — review and approve</p></div></button>;
  }

  // Needs follow-up
  if (onbStatus === 'needs_follow_up') {
    return <div className="space-y-2">
      <div className="flex items-center gap-2 p-3 bg-[#FDF2F2] rounded-xl border border-[#F0D4D4]"><AlertCircle className="w-4 h-4 text-[#B33A3A] shrink-0" strokeWidth={1.5} /><p className="text-xs text-[#B33A3A]">Application needs follow-up — re-upload required</p></div>
      <button onClick={() => onAction('send_application', enq)} className="text-[11px] px-3 py-1.5 rounded-full font-medium bg-white text-[#5C5C55] border border-[#E5E2DC] hover:bg-[#F2F0EB]" data-testid="wf-resend-app">Resend application link</button>
    </div>;
  }

  // Step 3: Send licence
  if (onbStatus === 'approved' && licStatus === 'not_sent') {
    return <button onClick={() => onAction('send_licence', enq)} className="w-full flex items-center gap-3 p-3 rounded-xl bg-[#3A4035] text-white hover:bg-[#2B3027] transition-colors" data-testid="wf-send-licence"><FileText className="w-4 h-4 shrink-0" strokeWidth={1.5} /><div className="text-left"><p className="text-xs font-medium">Send Licence for Signature</p><p className="text-[10px] text-white/60">Dropbox Sign — digital licence agreement</p></div></button>;
  }

  // Waiting for signature
  if (licStatus === 'sent_for_signature' || s === 'licence_sent') {
    return <div className="space-y-2">
      <div className="flex items-center gap-2 p-3 bg-[#FDFAF0] rounded-xl border border-[#EDE4C8]"><CalendarClock className="w-4 h-4 text-[#8A7230] shrink-0" strokeWidth={1.5} /><p className="text-xs text-[#8A7230]">Licence sent — waiting for signature</p></div>
      <button onClick={() => onAction('mark_signed', enq)} className="text-[11px] px-3 py-1.5 rounded-full font-medium bg-[#3A4035] text-white hover:bg-[#2B3027] flex items-center gap-1.5" data-testid="wf-mark-signed"><Check className="w-3 h-3" strokeWidth={2} />Mark as Signed</button>
    </div>;
  }

  // Step 4: Request payment
  if ((licStatus === 'signed' || s === 'licence_signed') && payStatus === 'not_requested') {
    return <button onClick={() => onAction('request_payment', enq)} className="w-full flex items-center gap-3 p-3 rounded-xl bg-[#3A4035] text-white hover:bg-[#2B3027] transition-colors" data-testid="wf-request-payment"><CreditCard className="w-4 h-4 shrink-0" strokeWidth={1.5} /><div className="text-left"><p className="text-xs font-medium">Request Payment</p><p className="text-[10px] text-white/60">Bank transfer — first month's rent</p></div></button>;
  }

  // Waiting for payment
  if (payStatus === 'payment_pending' || s === 'payment_pending') {
    return <div className="space-y-2">
      <div className="flex items-center gap-2 p-3 bg-[#FDFAF0] rounded-xl border border-[#EDE4C8]"><CreditCard className="w-4 h-4 text-[#8A7230] shrink-0" strokeWidth={1.5} /><p className="text-xs text-[#8A7230]">Payment requested — awaiting bank transfer</p></div>
      <button onClick={() => onAction('confirm_payment', enq)} className="text-[11px] px-3 py-1.5 rounded-full font-medium bg-[#3A4035] text-white hover:bg-[#2B3027] flex items-center gap-1.5" data-testid="wf-confirm-payment"><Check className="w-3 h-3" strokeWidth={2} />Confirm Payment Received</button>
    </div>;
  }

  // Step 5: Issue access
  if (payStatus === 'payment_confirmed' || s === 'payment_confirmed') {
    return <button onClick={() => onAction('issue_access', enq)} className="w-full flex items-center gap-3 p-3 rounded-xl bg-[#3A4035] text-white hover:bg-[#2B3027] transition-colors" data-testid="wf-issue-access"><Key className="w-4 h-4 shrink-0" strokeWidth={1.5} /><div className="text-left"><p className="text-xs font-medium">Issue Access</p><p className="text-[10px] text-white/60">Digital key + physical key handover</p></div></button>;
  }

  return null;
}


function CollapsedWorkflowAction({ enq, reservation, onAction }) {
  const s = enq.status || '';
  const appStatus = reservation?.application_status || 'not_sent';
  const onbStatus = reservation?.onboarding_status || 'not_started';
  const licStatus = reservation?.licence_status || 'not_sent';
  const payStatus = reservation?.payment_status || 'not_requested';
  const accStatus = reservation?.access_status || 'not_issued';

  if (accStatus === 'issued' || s === 'active')
    return <span className="text-[10px] px-2.5 py-1 rounded-full font-semibold bg-[#E8EDE5] text-[#3A4035] shrink-0">Active</span>;

  if (s === 'reserved' && appStatus === 'not_sent')
    return <button onClick={(e) => { e.stopPropagation(); onAction('send_application', enq); }} className="text-[11px] px-3 py-1.5 rounded-full font-medium bg-[#3A4035] text-white hover:bg-[#2B3027] transition-colors shrink-0" data-testid={`collapsed-send-app-${enq.enquiry_id}`}>Send Application</button>;

  if (['application_sent'].includes(s) && !['under_review','approved'].includes(onbStatus))
    return <span className="text-[10px] px-2.5 py-1 rounded-full font-semibold bg-[#FDFAF0] text-[#8A7230] shrink-0">Awaiting applicant</span>;

  if (onbStatus === 'under_review')
    return <button onClick={(e) => { e.stopPropagation(); onAction('review', enq); }} className="text-[11px] px-3 py-1.5 rounded-full font-medium bg-[#FDFAF0] text-[#8A7230] border border-[#EDE4C8] hover:bg-[#F8F0D8] transition-colors shrink-0" data-testid={`collapsed-review-${enq.enquiry_id}`}>Review</button>;

  if (onbStatus === 'needs_follow_up')
    return <span className="text-[10px] px-2.5 py-1 rounded-full font-semibold bg-[#FDF2F2] text-[#B33A3A] shrink-0">Needs follow-up</span>;

  if (onbStatus === 'approved' && licStatus === 'not_sent')
    return <button onClick={(e) => { e.stopPropagation(); onAction('send_licence', enq); }} className="text-[11px] px-3 py-1.5 rounded-full font-medium bg-[#3A4035] text-white hover:bg-[#2B3027] transition-colors shrink-0" data-testid={`collapsed-send-licence-${enq.enquiry_id}`}>Send Licence</button>;

  if (licStatus === 'sent_for_signature' || s === 'licence_sent')
    return <button onClick={(e) => { e.stopPropagation(); onAction('mark_signed', enq); }} className="text-[11px] px-3 py-1.5 rounded-full font-medium bg-white text-[#3A4035] border border-[#E5E2DC] hover:bg-[#F2F0EB] transition-colors shrink-0" data-testid={`collapsed-mark-signed-${enq.enquiry_id}`}>Mark Signed</button>;

  if ((licStatus === 'signed' || s === 'licence_signed') && payStatus === 'not_requested')
    return <button onClick={(e) => { e.stopPropagation(); onAction('request_payment', enq); }} className="text-[11px] px-3 py-1.5 rounded-full font-medium bg-[#3A4035] text-white hover:bg-[#2B3027] transition-colors shrink-0" data-testid={`collapsed-req-payment-${enq.enquiry_id}`}>Request Payment</button>;

  if (payStatus === 'payment_pending')
    return <button onClick={(e) => { e.stopPropagation(); onAction('confirm_payment', enq); }} className="text-[11px] px-3 py-1.5 rounded-full font-medium bg-white text-[#3A4035] border border-[#E5E2DC] hover:bg-[#F2F0EB] transition-colors shrink-0" data-testid={`collapsed-confirm-pay-${enq.enquiry_id}`}>Confirm Payment</button>;

  if (payStatus === 'payment_confirmed')
    return <button onClick={(e) => { e.stopPropagation(); onAction('issue_access', enq); }} className="text-[11px] px-3 py-1.5 rounded-full font-medium bg-[#3A4035] text-white hover:bg-[#2B3027] transition-colors shrink-0" data-testid={`collapsed-issue-access-${enq.enquiry_id}`}>Issue Access</button>;

  return null;
}




// ── Viewing Checklist ──
function ViewingChecklist({ enq }) {
  const [checks, setChecks] = useState({});
  const items = [
    { key: 'show', label: 'Show the studio' },
    { key: 'explain', label: 'Explain how studios work' },
    { key: 'confirm', label: 'Confirm their service/business' },
    { key: 'availability', label: 'Discuss availability' },
    { key: 'vision', label: 'Ask if they can see themselves working here' },
  ];
  return (
    <div className="bg-[#FAF9F6] rounded-xl p-3 border border-[#F2F0EB] mt-2" data-testid="viewing-checklist">
      <p className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#8A8B85] mb-2">Viewing Guide</p>
      <div className="space-y-1.5">
        {items.map(item => (
          <button key={item.key} onClick={() => setChecks(c => ({ ...c, [item.key]: !c[item.key] }))}
            className="w-full flex items-center gap-2.5 text-left py-1 group" data-testid={`check-${item.key}`}>
            <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${checks[item.key] ? 'bg-[#3A4035] border-[#3A4035]' : 'border-[#D8DBD3] group-hover:border-[#8A8B85]'}`}>
              {checks[item.key] && <Check className="w-2.5 h-2.5 text-white" strokeWidth={2} />}
            </div>
            <span className={`text-xs transition-colors ${checks[item.key] ? 'text-[#8A8B85] line-through' : 'text-[#1A1A1A]'}`}>{item.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Follow-Up Template ──
function FollowUpTemplate({ enq, onNoteSaved }) {
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const location = enq.preferred_location || enq.preferred_site || 'our studio';
  const msg = `Hi ${enq.name},\n\nIt was great meeting you today.\n\nJust wanted to follow up after your visit to ${location}. Let me know if you have any questions — happy to help.\n\nIf you'd like, I can hold a studio for you while you decide.`;

  const handleCopy = () => {
    navigator.clipboard.writeText(msg).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }).catch(() => {});
  };

  const handleSaveAsNote = async () => {
    setSaving(true);
    try {
      const updated = await addEnquiryNote(enq.enquiry_id, `Follow-up sent: "${msg.substring(0, 100)}..."`);
      onNoteSaved(updated);
    } catch (err) { console.error(err); } finally { setSaving(false); }
  };

  return (
    <div className="bg-[#FDFAF0] rounded-xl p-3 border border-[#EDE4C8] mt-2" data-testid="followup-template">
      <p className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#8A7230] mb-2">Follow-up Message</p>
      <pre className="text-xs text-[#5C5C55] whitespace-pre-wrap leading-relaxed bg-white rounded-lg p-3 border border-[#F2F0EB] mb-2 font-body">{msg}</pre>
      <div className="flex gap-2">
        <button onClick={handleCopy} className={`text-[11px] px-3 py-1.5 rounded-full font-medium transition-colors ${copied ? 'bg-[#E8EDE5] text-[#3A4035]' : 'bg-white text-[#5C5C55] border border-[#E5E2DC] hover:bg-[#F2F0EB]'}`} data-testid="copy-followup">
          {copied ? 'Copied' : 'Copy message'}
        </button>
        <button onClick={handleSaveAsNote} disabled={saving} className="text-[11px] px-3 py-1.5 rounded-full font-medium bg-white text-[#5C5C55] border border-[#E5E2DC] hover:bg-[#F2F0EB]" data-testid="log-followup">
          {saving ? 'Saving...' : 'Log as note'}
        </button>
      </div>
    </div>
  );
}



// ── Application Link Section ──
function ApplicationLinkSection({ enq, reservation, onNoteAdded }) {
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState('');
  const token = reservation.application_token;
  const appUrl = `${window.location.origin}/apply?token=${token}`;
  const appStatus = reservation.application_status || 'not_sent';
  const lastSentAt = reservation.application_last_sent_at;
  const lastSentBy = reservation.application_last_sent_by_name;
  const lastMethod = reservation.application_last_sent_method;
  const name = enq.name || '';
  const phone = enq.phone || '';
  const email = enq.email || '';
  const expired = reservation.expiry_date && reservation.expiry_date < new Date().toISOString().split('T')[0] && reservation.status === 'active';

  const msg = `Hi ${name},\n\nGreat meeting you today.\n\nYou can secure your studio by completing the short application below:\n\n${appUrl}\n\nIt takes just a few minutes.\n\nLet me know if you have any questions.\n\nThanks,\nGo Freestyle Studios`;

  const logAction = async (method) => {
    setSending(method);
    try {
      const result = await sendApplicationLink(reservation.reservation_id, { method });
      // Refresh parent to get updated notes
      if (onNoteAdded) {
        const enriched = await getEnrichedEnquiries();
        const updated = (enriched.enquiries || []).find(e => e.enquiry_id === enq.enquiry_id);
        if (updated) onNoteAdded(updated);
      }
    } catch (err) { console.error(err); }
    finally { setSending(''); }
  };

  const handleCopy = async () => {
    try { await navigator.clipboard.writeText(appUrl); } catch (err) { console.error(err); }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    logAction('copy');
  };

  const handleWhatsApp = () => {
    if (!phone) return;
    const cleanPhone = phone.replace(/\s/g, '').replace(/^0/, '44');
    window.open(`https://wa.me/${cleanPhone}?text=${encodeURIComponent(msg)}`, '_blank');
    logAction('whatsapp');
  };

  const handleEmail = () => {
    if (!email) return;
    window.location.href = `mailto:${email}?subject=${encodeURIComponent('Studio application link')}&body=${encodeURIComponent(msg)}`;
    logAction('email');
  };

  return (
    <div className="px-4 sm:px-5 pb-3" data-testid="application-link-section">
      <div className="bg-[#FAF9F6] rounded-xl border border-[#F2F0EB] p-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#8A8B85] flex items-center gap-1.5">
            <ArrowRight className="w-3 h-3" strokeWidth={1.5} />Application Link
          </p>
          <span className={`text-[9px] px-2 py-0.5 rounded-full font-semibold ${appStatus === 'not_sent' ? 'bg-[#F2F0EB] text-[#8A8B85]' : appStatus === 'sent' ? 'bg-[#E8EDE5] text-[#3A4035]' : 'bg-[#E8EDE5] text-[#3A4035]'}`}>
            {appStatus === 'not_sent' ? 'Not sent' : appStatus === 'sent' ? 'Sent' : 'Resent'}
          </span>
        </div>

        {expired ? (
          <p className="text-xs text-[#B33A3A] mb-2">Application link expired</p>
        ) : (
          <>
            {/* URL display */}
            <div className="bg-white rounded-lg border border-[#E5E2DC] px-3 py-2 mb-3 flex items-center gap-2">
              <p className="text-[10px] text-[#5C5C55] truncate flex-1 font-mono">{appUrl}</p>
            </div>

            {/* Action buttons */}
            <div className="flex flex-wrap gap-2">
              <button onClick={handleCopy}
                className={`text-[11px] px-3 py-1.5 rounded-full font-medium transition-colors flex items-center gap-1.5 ${copied ? 'bg-[#E8EDE5] text-[#3A4035]' : 'bg-[#3A4035] text-white hover:bg-[#2B3027]'}`}
                data-testid="applink-copy">
                {copied ? <><Check className="w-3 h-3" strokeWidth={2} />Copied</> : 'Copy link'}
              </button>

              <button onClick={handleWhatsApp} disabled={!phone || sending === 'whatsapp'}
                className={`text-[11px] px-3 py-1.5 rounded-full font-medium border transition-colors flex items-center gap-1.5 ${phone ? 'bg-white text-[#3A4035] border-[#E5E2DC] hover:bg-[#F2F0EB]' : 'bg-[#F2F0EB] text-[#8A8B85] border-transparent cursor-not-allowed'}`}
                title={phone ? '' : 'Phone number required'}
                data-testid="applink-whatsapp">
                {sending === 'whatsapp' ? <Loader2 className="w-3 h-3 animate-spin" /> : null}WhatsApp
              </button>

              <button onClick={handleEmail} disabled={!email || sending === 'email'}
                className={`text-[11px] px-3 py-1.5 rounded-full font-medium border transition-colors flex items-center gap-1.5 ${email ? 'bg-white text-[#3A4035] border-[#E5E2DC] hover:bg-[#F2F0EB]' : 'bg-[#F2F0EB] text-[#8A8B85] border-transparent cursor-not-allowed'}`}
                title={email ? '' : 'Email required'}
                data-testid="applink-email">
                {sending === 'email' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" strokeWidth={1.5} />}Email
              </button>
            </div>

            {/* Missing contact data warnings */}
            {!phone && !email && <p className="text-[10px] text-[#8A8B85] mt-2">No phone or email — copy the link to share manually</p>}
            {!phone && email && <p className="text-[10px] text-[#8A8B85] mt-2">Phone number required to send via WhatsApp</p>}
          </>
        )}

        {/* Last sent info */}
        {lastSentAt && (
          <p className="text-[10px] text-[#8A8B85] mt-2 italic">
            Last {lastMethod === 'copy' ? 'copied' : `sent via ${lastMethod}`} by {lastSentBy} · {new Date(lastSentAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} {new Date(lastSentAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
          </p>
        )}
      </div>
    </div>
  );
}


function EnquiryCard({ enq, expanded, onToggle, onUpdate, onOpenBooking, onOpenOutcome, onOpenReserve, onOpenConfirm, onOpenOnboarding, onOpenSign, onOpenReview, onWorkflowAction }) {
  const [noteText, setNoteText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [followUpDate, setFollowUpDate] = useState(enq.follow_up_date || '');
  const [showNextFU, setShowNextFU] = useState(false);
  const [nextFUDate, setNextFUDate] = useState('');
  const latestNote = (enq.notes || []).length > 0 ? enq.notes[enq.notes.length - 1] : null;
  const tags = enq.tags || [];
  const notes = enq.notes || [];
  const temp = tempConfig[enq.lead_temperature] || tempConfig.warm;
  const stage = STAGES.find(s => s.key === enq.status) || STAGES[0];
  const staleWarn = daysSince(enq.last_contacted_at) >= 7 && enq.status !== 'signed';
  const stale3 = daysSince(enq.last_contacted_at) >= 3 && !staleWarn && enq.status !== 'signed';
  const fuOverdue = isOverdue(enq);
  const viewings = enq.viewings || [];
  const nextViewing = viewings.find(v => v.status === 'scheduled');
  const suggestedStudios = enq.suggested_studios || [];
  const reservation = enq.reservation;

  const handlePrimaryAction = () => {
    if (enq.status === 'new') { updateEnquiry(enq.enquiry_id, { status: 'contacted' }).then(onUpdate).catch(console.error); }
    else if (enq.status === 'contacted') { onOpenBooking(enq); }
    else if (enq.status === 'viewing_booked') { onOpenOutcome(enq); }
    // Post-reservation stages handled by WorkflowAction component
  };

  const handleAddNote = async () => { if (!noteText.trim()) return; setSubmitting(true); try { onUpdate(await addEnquiryNote(enq.enquiry_id, noteText.trim())); setNoteText(''); } catch (err) { console.error(err); } finally { setSubmitting(false); } };
  const handleToggleTag = async (tag) => { const nt = tags.includes(tag) ? tags.filter(t => t !== tag) : [...tags, tag]; try { onUpdate(await updateEnquiryTags(enq.enquiry_id, nt)); } catch (err) { console.error(err); } };
  const handleFollowUp = async (r, d) => { const nd = r && !d ? new Date().toISOString().split('T')[0] : (d||''); try { const u = await updateEnquiryFollowup(enq.enquiry_id, {follow_up_required:r,follow_up_date:nd}); onUpdate(u); setFollowUpDate(nd); } catch (err) { console.error(err); } };
  const handleCompleteFU = async () => { try { onUpdate(await updateEnquiryFollowup(enq.enquiry_id, {follow_up_required:false,follow_up_date:''})); setShowNextFU(true); } catch (err) { console.error(err); } };
  const handleSetNextFU = async () => { if (!nextFUDate) return; try { const u = await updateEnquiryFollowup(enq.enquiry_id, {follow_up_required:true,follow_up_date:nextFUDate}); onUpdate(u); setFollowUpDate(nextFUDate); setShowNextFU(false); setNextFUDate(''); } catch (err) { console.error(err); } };
  const handleTemp = async (t) => { try { onUpdate(await updateEnquiryTemperature(enq.enquiry_id, t)); } catch (err) { console.error(err); } };
  const handleStatus = async (st) => { try { onUpdate(await updateEnquiry(enq.enquiry_id, {status:st})); } catch (err) { console.error(err); } };

  return (
    <div className={`bg-white rounded-2xl border shadow-[0_2px_10px_rgba(0,0,0,0.02)] overflow-hidden transition-all ${staleWarn ? 'border-[#F0D4D4]' : stale3 ? 'border-[#EDE4C8]' : 'border-[#E5E2DC]'}`} data-testid={`enquiry-card-${enq.enquiry_id}`}>
      {/* COLLAPSED */}
      <div className="p-4 sm:p-5 flex items-center gap-3">
        <button className="flex items-center gap-3 flex-1 min-w-0 text-left" onClick={onToggle} data-testid={`enquiry-toggle-${enq.enquiry_id}`}>
          <div className="w-9 h-9 rounded-full bg-[#F2F0EB] flex items-center justify-center text-xs font-semibold text-[#5C5C55] shrink-0">{enq.name?.charAt(0)}</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <p className="text-sm font-medium text-[#1A1A1A] truncate">{enq.name}</p>
              <span className={`text-[8px] px-1.5 py-0.5 rounded-full font-bold uppercase shrink-0 ${temp.bg} ${temp.text}`}>{temp.label}</span>
              {staleWarn && <span className="text-[8px] px-1.5 py-0.5 rounded-full font-bold bg-[#FDF2F2] text-[#B33A3A] shrink-0 flex items-center gap-0.5"><AlertCircle className="w-2.5 h-2.5" strokeWidth={2} />7d+</span>}
              {stale3 && !staleWarn && <span className="text-[8px] px-1.5 py-0.5 rounded-full font-bold bg-[#FDFAF0] text-[#8A7230] shrink-0">3d+</span>}
              {fuOverdue && <span className="text-[8px] px-1.5 py-0.5 rounded-full font-bold bg-[#FDFAF0] text-[#8A7230] shrink-0">FU overdue</span>}
            </div>
            <p className="text-[11px] text-[#8A8B85] truncate mt-0.5">{enq.business_type}{enq.preferred_location ? ` · ${enq.preferred_location}` : ''}</p>
            <div className="flex items-center gap-2 mt-0.5">
              {enq.last_contacted_at && <span className="text-[10px] text-[#8A8B85] flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" strokeWidth={1.5} />{timeAgo(enq.last_contacted_at)}</span>}
              {nextViewing && <span className="text-[10px] text-[#3A4035] font-medium flex items-center gap-0.5"><Calendar className="w-2.5 h-2.5" strokeWidth={1.5} />{nextViewing.scheduled_date} {nextViewing.scheduled_time}</span>}
              {reservation && <span className="text-[10px] text-[#3A4035] font-medium flex items-center gap-0.5"><DoorOpen className="w-2.5 h-2.5" strokeWidth={1.5} />{reservation.studio_name || 'Reserved'} · exp {reservation.expiry_date}</span>}
              {latestNote && !expanded && <p className="text-[10px] text-[#8A8B85] truncate italic">{latestNote.text}</p>}
            </div>
          </div>
        </button>
        <div className="flex items-center gap-2 shrink-0">
          <div className="hidden sm:flex items-center gap-1">{tags.slice(0,2).map(t => <span key={t} className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold border ${tagColors[t]||'bg-[#F2F0EB] text-[#5C5C55] border-[#E5E2DC]'}`}>{t.replace(/_/g,' ')}</span>)}</div>
          {stage.action && <button onClick={handlePrimaryAction} className="text-[11px] px-3 py-1.5 rounded-full font-medium bg-[#3A4035] text-white hover:bg-[#2B3027] transition-colors shrink-0" data-testid={`primary-action-${enq.enquiry_id}`}>{stage.action}</button>}
          {reservation && !stage.action && <CollapsedWorkflowAction enq={enq} reservation={reservation} onAction={onWorkflowAction} />}
          <button onClick={onToggle} className="p-1">{expanded ? <ChevronUp className="w-4 h-4 text-[#8A8B85]" strokeWidth={1.5}/> : <ChevronDown className="w-4 h-4 text-[#8A8B85]" strokeWidth={1.5}/>}</button>
        </div>
      </div>
      {/* EXPANDED */}
      {expanded && (
        <div className="border-t border-[#F2F0EB]">
          {/* Viewing context + checklist */}
          {viewings.length > 0 && (
            <div className="px-4 sm:px-5 pt-3 pb-2">
              <p className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#8A8B85] mb-2 flex items-center gap-1.5"><Calendar className="w-3 h-3" strokeWidth={1.5}/>Viewings</p>
              <div className="flex flex-wrap gap-2 mb-2">{viewings.map(v => (
                <div key={v.viewing_id} className={`text-[11px] px-3 py-1.5 rounded-xl border ${v.status==='scheduled' ? 'bg-[#F0F2EF] border-[#D8DBD3] text-[#3A4035]' : v.outcome==='interested' ? 'bg-[#E8EDE5] border-[#D0D9CB] text-[#3A4035]' : 'bg-[#F2F0EB] border-[#E5E2DC] text-[#8A8B85]'}`}>
                  {v.scheduled_date} at {v.scheduled_time} · <span className="capitalize">{v.status}</span>{v.outcome ? ` · ${v.outcome.replace('_',' ')}` : ''}
                  {v.interest_level && <span className="ml-1.5 opacity-70">· {v.interest_level}</span>}
                  {v.hesitation && v.hesitation !== 'None' && <span className="ml-1.5 opacity-70">· {v.hesitation}</span>}
                </div>
              ))}</div>
              {/* Viewing checklist (only for scheduled viewings) */}
              {nextViewing && enq.status === 'viewing_booked' && (
                <ViewingChecklist enq={enq} />
              )}
              {/* Follow-up template (after completed viewing with needs_time) */}
              {viewings.some(v => v.status === 'completed' && v.outcome === 'needs_time') && !reservation && (
                <FollowUpTemplate enq={enq} onNoteSaved={onUpdate} />
              )}
            </div>
          )}
          {/* ── WORKFLOW TRACKER ── */}
          {reservation && (
            <div className="px-4 sm:px-5 pt-2 pb-3">
              <p className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#8A8B85] mb-2 flex items-center gap-1.5"><DoorOpen className="w-3 h-3" strokeWidth={1.5}/>Onboarding Workflow</p>
              <div className="flex items-center gap-2 flex-wrap mb-3">
                <span className="text-[11px] px-3 py-1.5 rounded-xl bg-[#E8EDE5] border border-[#D0D9CB] text-[#3A4035] font-medium">{reservation.studio_name || reservation.studio_id}</span>
                <span className="text-[10px] text-[#8A8B85]">Exp {reservation.expiry_date}</span>
              </div>

              {/* Step progress bar */}
              <WorkflowProgress reservation={reservation} enquiryStatus={enq.status} contract={enq.contract} />

              {/* Next action — only shows the ONE valid action */}
              <div className="mt-3">
                <WorkflowAction enq={enq} reservation={reservation} onAction={onWorkflowAction} />
              </div>
            </div>
          )}

          {/* ── APPLICATION LINK SECTION ── */}
          {reservation?.application_token && (
            <ApplicationLinkSection enq={enq} reservation={reservation} onNoteAdded={onUpdate} />
          )}
          {/* Suggested studios */}
          {suggestedStudios.length > 0 && enq.status !== 'reserved' && enq.status !== 'signed' && (
            <div className="px-4 sm:px-5 pt-2 pb-2">
              <p className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#8A8B85] mb-2 flex items-center gap-1.5"><DoorOpen className="w-3 h-3" strokeWidth={1.5}/>Suggested Studios</p>
              <div className="flex gap-2 flex-wrap">{suggestedStudios.map(s => (
                <div key={s.studio_id} className="text-[11px] px-3 py-2 rounded-xl border border-[#E5E2DC] bg-[#FAF9F6] flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full shrink-0 ${s.status==='vacant'?'bg-[#D8DBD3]':'bg-[#C4A44E]'}`}/><span className="font-medium text-[#1A1A1A]">{s.name}</span><span className="text-[#8A8B85]">£{s.monthly_rent}/mo · {s.site_name}</span>
                </div>
              ))}</div>
            </div>
          )}
          {/* Contact info */}
          <div className="px-4 sm:px-5 pt-3 pb-2">
            <div className="flex flex-wrap gap-3 text-xs text-[#5C5C55]">
              <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" strokeWidth={1.5}/>{enq.email}</span>
              <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" strokeWidth={1.5}/>{enq.phone}</span>
              {(enq.preferred_location||enq.preferred_site) && <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" strokeWidth={1.5}/>{enq.preferred_location||enq.preferred_site}</span>}
              <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" strokeWidth={1.5}/>Last: {enq.last_contacted_at ? formatDT(enq.last_contacted_at) : 'Never'}</span>
            </div>
            {enq.message && <p className="text-xs text-[#5C5C55] bg-[#F2F0EB] rounded-xl p-3 leading-relaxed mt-3">{enq.message}</p>}
          </div>
          {/* Temp + Tags */}
          <div className="px-4 sm:px-5 pb-3 flex flex-col sm:flex-row gap-4">
            <div className="shrink-0">
              <p className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#8A8B85] mb-2 flex items-center gap-1.5"><Thermometer className="w-3 h-3" strokeWidth={1.5}/>Temperature</p>
              <div className="flex gap-1.5">{['cold','warm','hot'].map(t => { const c=tempConfig[t]; const a=enq.lead_temperature===t; return <button key={t} onClick={()=>handleTemp(t)} className={`text-[11px] px-3 py-1.5 rounded-full font-semibold border transition-all ${a?`${c.bg} ${c.text} ${c.border}`:'bg-white text-[#8A8B85] border-[#E5E2DC]'}`} data-testid={`temp-${t}`}><span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 ${a?c.dot:'bg-[#D8DBD3]'}`}/>{c.label}</button>; })}</div>
            </div>
            <div className="flex-1">
              <p className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#8A8B85] mb-2 flex items-center gap-1.5"><Tag className="w-3 h-3" strokeWidth={1.5}/>Tags</p>
              <div className="flex flex-wrap gap-1.5">{SUGGESTED_TAGS.map(tag => { const a=tags.includes(tag); return <button key={tag} onClick={()=>handleToggleTag(tag)} className={`text-[11px] px-2.5 py-1 rounded-full font-medium border transition-all ${a?(tagColors[tag]||'bg-[#E8EDE5] text-[#3A4035] border-[#D0D9CB]'):'bg-white text-[#8A8B85] border-[#E5E2DC]'}`} data-testid={`tag-${tag}`}>{a&&<Check className="w-2.5 h-2.5 inline mr-1" strokeWidth={2}/>}{tag.replace(/_/g,' ')}</button>;})}</div>
            </div>
          </div>
          {/* Follow-up */}
          <div className="px-4 sm:px-5 pb-3">
            <p className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#8A8B85] mb-2 flex items-center gap-1.5"><CalendarClock className="w-3 h-3" strokeWidth={1.5}/>Follow-up</p>
            {enq.follow_up_required ? (
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[11px] px-3 py-1.5 rounded-full font-medium border ${fuOverdue?'bg-[#FDF2F2] text-[#B33A3A] border-[#F0D4D4]':'bg-[#FDFAF0] text-[#8A7230] border-[#EDE4C8]'}`}>{fuOverdue?'Overdue':'Due'}: {followUpDate}</span>
                <input type="date" value={followUpDate} onChange={e=>{setFollowUpDate(e.target.value);handleFollowUp(true,e.target.value);}} className="text-xs bg-[#F2F0EB] border-transparent rounded-lg px-3 py-1.5 outline-none" data-testid="followup-date"/>
                <button onClick={handleCompleteFU} className="text-[11px] px-3 py-1.5 rounded-full font-medium bg-[#3A4035] text-white hover:bg-[#2B3027] flex items-center gap-1" data-testid="complete-followup"><Check className="w-3 h-3" strokeWidth={2}/>Done</button>
              </div>
            ) : showNextFU ? (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[11px] text-[#3A4035] font-medium">Schedule next?</span>
                <input type="date" value={nextFUDate} onChange={e=>setNextFUDate(e.target.value)} className="text-xs bg-[#F2F0EB] border-transparent rounded-lg px-3 py-1.5 outline-none" data-testid="next-followup-date"/>
                <button onClick={handleSetNextFU} disabled={!nextFUDate} className="text-[11px] px-3 py-1.5 rounded-full font-medium bg-[#3A4035] text-white disabled:opacity-40" data-testid="set-next-followup">Set</button>
                <button onClick={()=>setShowNextFU(false)} className="text-[11px] text-[#8A8B85] px-2 py-1" data-testid="skip-next-followup">Skip</button>
              </div>
            ) : <button onClick={()=>handleFollowUp(true,'')} className="text-[11px] px-3 py-1.5 rounded-full font-medium bg-[#F2F0EB] text-[#8A8B85] border border-[#E5E2DC]" data-testid="toggle-followup">Set follow-up</button>}
          </div>
          {/* Notes */}
          <div className="px-4 sm:px-5 pb-4">
            <p className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#8A8B85] mb-3 flex items-center gap-1.5"><MessageSquare className="w-3 h-3" strokeWidth={1.5}/>Notes ({notes.length})</p>
            {notes.length > 0 && <div className="space-y-2 mb-3 max-h-48 overflow-y-auto">{[...notes].reverse().map(n => <div key={n.note_id} className="bg-[#FAF9F6] rounded-xl p-3 border border-[#F2F0EB]" data-testid={`note-${n.note_id}`}><p className="text-xs text-[#1A1A1A] leading-relaxed">{n.text}</p><p className="text-[10px] text-[#8A8B85] mt-1.5">{n.author} · {formatDT(n.created_at)}</p></div>)}</div>}
            <div className="flex gap-2">
              <Textarea value={noteText} onChange={e=>setNoteText(e.target.value)} placeholder="Add a note..." rows={2} className="flex-1 bg-[#F2F0EB] border-transparent rounded-xl text-xs placeholder:text-[#8A8B85] resize-none" data-testid="note-input" onKeyDown={e=>{if(e.key==='Enter'&&(e.metaKey||e.ctrlKey))handleAddNote();}}/>
              <Button onClick={handleAddNote} disabled={submitting||!noteText.trim()} size="sm" className="bg-[#3A4035] text-white rounded-xl px-3 h-auto self-end hover:bg-[#2B3027] shrink-0" data-testid="add-note-button">{submitting?<Loader2 className="w-3.5 h-3.5 animate-spin"/>:<Send className="w-3.5 h-3.5" strokeWidth={1.5}/>}</Button>
            </div>
          </div>
          {/* Stage bar */}
          <div className="px-4 sm:px-5 pb-5 pt-2 border-t border-[#F2F0EB]">
            <p className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#8A8B85] mb-2">Stage</p>
            <div className="flex flex-wrap gap-1.5">{STAGES.map((s,i)=>{const a=enq.status===s.key;return <button key={s.key} onClick={()=>handleStatus(s.key)} className={`text-[11px] px-3 py-1.5 rounded-full font-medium transition-all flex items-center gap-1.5 ${a?`${s.color} ${s.text} shadow-sm`:'bg-[#F2F0EB] text-[#8A8B85] hover:bg-[#E5E7E1]'}`} data-testid={`enquiry-move-${s.key}`}>{i>0&&!a&&<ArrowRight className="w-2.5 h-2.5" strokeWidth={2}/>}{s.label}</button>;})}</div>
          </div>
          {/* Quick actions */}
          {enq.status !== 'signed' && (
            <div className="px-4 sm:px-5 pb-4 flex flex-wrap gap-2">
              {enq.status === 'contacted' && <button onClick={()=>onOpenBooking(enq)} className="text-[11px] px-3 py-1.5 rounded-full font-medium bg-[#3A4035] text-white hover:bg-[#2B3027] flex items-center gap-1.5" data-testid="action-book-viewing"><Calendar className="w-3 h-3" strokeWidth={1.5}/>Book Viewing</button>}
              {enq.status === 'viewing_booked' && <button onClick={()=>onOpenOutcome(enq)} className="text-[11px] px-3 py-1.5 rounded-full font-medium bg-[#3A4035] text-white hover:bg-[#2B3027] flex items-center gap-1.5" data-testid="action-complete-viewing"><Check className="w-3 h-3" strokeWidth={1.5}/>Complete Viewing</button>}
              {(enq.status === 'viewing_booked' || enq.status === 'contacted') && <button onClick={()=>onOpenReserve(enq)} className="text-[11px] px-3 py-1.5 rounded-full font-medium bg-[#E8EDE5] text-[#3A4035] hover:bg-[#D8DBD3] flex items-center gap-1.5" data-testid="action-reserve"><DoorOpen className="w-3 h-3" strokeWidth={1.5}/>Reserve Studio</button>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Page ──
export default function EnquiriesPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeStage = searchParams.get('filter') || searchParams.get('stage') || 'all';
  const viewMode = searchParams.get('view') || '';
  const [bookingEnq, setBookingEnq] = useState(null);
  const [outcomeEnq, setOutcomeEnq] = useState(null);
  const [reserveEnq, setReserveEnq] = useState(null);
  const [onboardingEnq, setOnboardingEnq] = useState(null);
  const [signEnq, setSignEnq] = useState(null);
  const [reviewEnq, setReviewEnq] = useState(null);
  const [showCreateEnq, setShowCreateEnq] = useState(false);

  const loadData = async () => { try { setData(await getEnrichedEnquiries()); } catch(e){console.error(e);} finally{setLoading(false);} };
  useEffect(() => { loadData(); }, []);

  const enquiries = data?.enquiries || [];
  const sites = data?.sites || [];
  const studios = data?.studios || [];

  const handleUpdate = (updated) => { setData(prev => ({ ...prev, enquiries: (prev?.enquiries||[]).map(e => e.enquiry_id === updated.enquiry_id ? {...e,...updated} : e) })); };
  const handleRefresh = () => { loadData(); };

  const handleWorkflowAction = async (action, enq) => {
    const res = enq.reservation;
    try {
      switch (action) {
        case 'send_application':
          const appResult = await sendApplicationLink(res.reservation_id, { method: 'manual' });
          const appUrl = `${window.location.origin}/apply?token=${appResult.application_token}`;
          try { await navigator.clipboard.writeText(appUrl); } catch (err) { console.error(err); }
          alert(`Application link copied:\n${appUrl}`);
          break;
        case 'review':
          setReviewEnq(enq);
          return;
        case 'send_licence':
          await sendLicence(res.reservation_id);
          break;
        case 'mark_signed':
          if (enq.contract?.contract_id) await markLicenceSigned(enq.contract.contract_id);
          break;
        case 'request_payment':
          await requestPaymentAction(res.reservation_id);
          break;
        case 'confirm_payment':
          await confirmPaymentAction(res.reservation_id);
          break;
        case 'issue_access':
          await issueAccess(res.reservation_id, { digital_access_status: 'issued', physical_key_status: 'issued', notes: '' });
          break;
        default: break;
      }
      handleRefresh();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.detail || 'Action failed');
    }
  };

  const setStageFilter = (v) => { v==='all' ? setSearchParams({}) : setSearchParams({stage:v}); };
  let filtered = enquiries;
  if (viewMode === 'followups') filtered = enquiries.filter(e => e.follow_up_required);
  else if (viewMode === 'stale') filtered = enquiries.filter(e => daysSince(e.last_contacted_at) >= 7 && e.status !== 'signed');
  else if (activeStage !== 'all') filtered = enquiries.filter(e => e.status === activeStage);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-[#3A4035] animate-spin" /></div>;

  const counts = {}; STAGES.forEach(s=>{counts[s.key]=enquiries.filter(e=>e.status===s.key).length;});
  const total = enquiries.length;
  const followupsCount = enquiries.filter(e => e.follow_up_required).length;
  const staleCount = enquiries.filter(e => daysSince(e.last_contacted_at) >= 7 && e.status !== 'signed').length;
  const todayStr = new Date().toISOString().split('T')[0];
  const fuDueToday = enquiries.filter(e => e.follow_up_required && e.follow_up_date && e.follow_up_date <= todayStr).length;
  const viewingsToday = enquiries.reduce((c,e)=>c+(e.viewings||[]).filter(v=>v.scheduled_date===todayStr&&v.status==='scheduled').length,0);
  const tempCounts = {cold:0,warm:0,hot:0}; enquiries.forEach(e=>{const t=e.lead_temperature||'warm';if(tempCounts[t]!==undefined)tempCounts[t]++;});

  return (
    <div className="animate-fade-in" data-testid="enquiries-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-medium text-[#1A1A1A] tracking-tight">Enquiry Pipeline</h1>
          <div className="flex items-center gap-3 mt-1.5 flex-wrap">
            <p className="text-sm text-[#5C5C55]">{total} total</p>
          {tempCounts.hot>0&&<span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-[#FDF2F2] text-[#B33A3A]">{tempCounts.hot} hot</span>}
          {tempCounts.warm>0&&<span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-[#FDFAF0] text-[#8A7230]">{tempCounts.warm} warm</span>}
          {tempCounts.cold>0&&<span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-[#E8EDF2] text-[#4A6080]">{tempCounts.cold} cold</span>}
        </div>
        </div>
        <Button onClick={() => setShowCreateEnq(true)} className="bg-[#3A4035] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[#2B3027] hidden sm:flex" data-testid="add-enquiry-button">
          <Plus className="w-4 h-4 mr-1.5" strokeWidth={1.5} />Add Enquiry
        </Button>
      </div>

      {/* Summary */}
      {(fuDueToday>0||viewingsToday>0||staleCount>0)&&(
        <div className="flex gap-2 mb-5 overflow-x-auto pb-1" data-testid="top-summary">
          {fuDueToday>0&&<button onClick={()=>setSearchParams({view:'followups'})} className="shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-[#FDFAF0] border border-[#EDE4C8]" data-testid="summary-followups"><CalendarClock className="w-4 h-4 text-[#8A7230]" strokeWidth={1.5}/><span className="text-xs font-medium text-[#8A7230]">{fuDueToday} follow-up{fuDueToday>1?'s':''} due</span></button>}
          {viewingsToday>0&&<div className="shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-[#F0F2EF] border border-[#D8DBD3]" data-testid="summary-viewings"><Calendar className="w-4 h-4 text-[#3A4035]" strokeWidth={1.5}/><span className="text-xs font-medium text-[#3A4035]">{viewingsToday} viewing{viewingsToday>1?'s':''} today</span></div>}
          {staleCount>0&&<button onClick={()=>setSearchParams({view:'stale'})} className="shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-[#FDF2F2] border border-[#F0D4D4]" data-testid="summary-stale"><AlertCircle className="w-4 h-4 text-[#B33A3A]" strokeWidth={1.5}/><span className="text-xs font-medium text-[#B33A3A]">{staleCount} stale (7d+)</span></button>}
        </div>
      )}

      {/* Filter bar */}
      <div className="bg-white rounded-2xl border border-[#E5E2DC] p-4 sm:p-5 mb-6" data-testid="pipeline-visualisation">
        <div className="flex items-center gap-1 mb-4 overflow-x-auto pb-1">
          <button onClick={()=>setSearchParams({})} className={`shrink-0 text-[11px] px-3 py-1.5 rounded-full font-semibold transition-colors ${activeStage==='all'&&!viewMode?'bg-[#1A1A1A] text-white':'bg-[#F2F0EB] text-[#5C5C55] hover:bg-[#E5E7E1]'}`} data-testid="pipeline-filter-all">All ({total})</button>
          {STAGES.map((s,i)=>(<React.Fragment key={s.key}>{i>0&&<ChevronRight className="w-3 h-3 text-[#D8DBD3] shrink-0" strokeWidth={2}/>}<button onClick={()=>setStageFilter(s.key)} className={`shrink-0 text-[11px] px-3 py-1.5 rounded-full font-semibold transition-colors ${activeStage===s.key&&!viewMode?`${s.color} ${s.text}`:'bg-[#F2F0EB] text-[#5C5C55] hover:bg-[#E5E7E1]'}`} data-testid={`pipeline-filter-${s.key}`}>{s.label} ({counts[s.key]||0})</button></React.Fragment>))}
          <div className="w-px h-5 bg-[#E5E2DC] shrink-0 mx-1"/>
          <button onClick={()=>viewMode==='followups'?setSearchParams({}):setSearchParams({view:'followups'})} className={`shrink-0 text-[11px] px-3 py-1.5 rounded-full font-semibold flex items-center gap-1.5 ${viewMode==='followups'?'bg-[#FDFAF0] text-[#8A7230] border border-[#EDE4C8]':'bg-[#F2F0EB] text-[#5C5C55]'}`} data-testid="pipeline-filter-followups"><CalendarClock className="w-3 h-3" strokeWidth={1.5}/>Follow-ups ({followupsCount})</button>
        </div>
        <div className="flex h-2.5 rounded-full overflow-hidden bg-[#F2F0EB]">{STAGES.map(s=>{const c=counts[s.key]||0;const p=total>0?(c/total)*100:0;return p===0?null:<div key={s.key} className={`${s.dot} transition-all duration-500`} style={{width:`${p}%`}} title={`${s.label}: ${c}`}/>;})}</div>
      </div>

      {/* Cards grouped or flat */}
      {activeStage==='all'&&!viewMode ? (
        STAGES.map(stage=>{const se=filtered.filter(e=>e.status===stage.key);if(se.length===0)return null;return(
          <div key={stage.key} className="mb-6" data-testid={`stage-group-${stage.key}`}>
            <div className="flex items-center gap-2 mb-2.5"><span className={`w-2.5 h-2.5 rounded-full ${stage.dot}`}/><p className="text-xs tracking-[0.05em] uppercase font-semibold text-[#5C5C55]">{stage.label}</p><span className="text-xs text-[#8A8B85]">({se.length})</span></div>
            <div className="space-y-2">{se.map(enq=><EnquiryCard key={enq.enquiry_id} enq={enq} expanded={expandedId===enq.enquiry_id} onToggle={()=>setExpandedId(expandedId===enq.enquiry_id?null:enq.enquiry_id)} onUpdate={handleUpdate} onOpenBooking={setBookingEnq} onOpenOutcome={setOutcomeEnq} onOpenReserve={setReserveEnq} onOpenConfirm={()=>{}} onOpenOnboarding={()=>{}} onOpenSign={()=>{}} onOpenReview={setReviewEnq} onWorkflowAction={handleWorkflowAction}/>)}</div>
          </div>);})
      ) : (
        <div className="space-y-2.5">{filtered.map(enq=><EnquiryCard key={enq.enquiry_id} enq={enq} expanded={expandedId===enq.enquiry_id} onToggle={()=>setExpandedId(expandedId===enq.enquiry_id?null:enq.enquiry_id)} onUpdate={handleUpdate} onOpenBooking={setBookingEnq} onOpenOutcome={setOutcomeEnq} onOpenReserve={setReserveEnq} onOpenConfirm={()=>{}} onOpenOnboarding={()=>{}} onOpenSign={()=>{}} onOpenReview={setReviewEnq} onWorkflowAction={handleWorkflowAction}/>)}</div>
      )}

      {filtered.length===0&&<div className="text-center py-16"><ClipboardList className="w-10 h-10 text-[#D8DBD3] mx-auto mb-3" strokeWidth={1.5}/><p className="text-sm text-[#8A8B85]">{viewMode==='followups'?'No follow-ups pending':viewMode==='stale'?'No stale leads':'No enquiries'}</p><button onClick={()=>setSearchParams({})} className="text-xs text-[#3A4035] font-medium mt-2 hover:underline" data-testid="clear-filter">View all</button></div>}

      {/* Modals */}
      {bookingEnq && <BookViewingModal open={!!bookingEnq} onClose={()=>setBookingEnq(null)} enq={bookingEnq} sites={sites} studios={studios} onBooked={handleRefresh}/>}
      {outcomeEnq && <ViewingOutcomeModal open={!!outcomeEnq} onClose={()=>setOutcomeEnq(null)} enq={outcomeEnq} onComplete={()=>handleRefresh()} onReserveAfter={setReserveEnq}/>}
      {reserveEnq && <ReserveStudioModal open={!!reserveEnq} onClose={()=>setReserveEnq(null)} enq={reserveEnq} studios={studios} sites={sites} onReserved={handleRefresh}/>}
      {onboardingEnq && <OnboardingModal open={!!onboardingEnq} onClose={()=>setOnboardingEnq(null)} enq={onboardingEnq} onDone={handleRefresh}/>}
      {reviewEnq && <ReviewOnboardingModal open={!!reviewEnq} onClose={()=>setReviewEnq(null)} enq={reviewEnq} onDone={handleRefresh}/>}
      <CreateEnquiryModal open={showCreateEnq} onClose={() => setShowCreateEnq(false)} onCreated={() => { setShowCreateEnq(false); handleRefresh(); }} />

      {/* Mobile FAB */}
      <button onClick={() => setShowCreateEnq(true)}
        className="sm:hidden fixed bottom-24 right-4 z-40 w-14 h-14 bg-[#3A4035] text-white rounded-full shadow-[0_4px_20px_rgba(0,0,0,0.15)] flex items-center justify-center hover:bg-[#2B3027] active:scale-95 transition-all"
        data-testid="add-enquiry-fab">
        <Plus className="w-6 h-6" strokeWidth={1.5} />
      </button>
    </div>
  );
}

// ── Create Enquiry Modal ──
function CreateEnquiryModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', business_type: '', preferred_location: '', source: '', message: '' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name || (!form.email && !form.phone)) return;
    setSaving(true);
    try {
      await createEnquiry({ ...form, preferred_site: form.preferred_location });
      onCreated();
      setForm({ name: '', email: '', phone: '', business_type: '', preferred_location: '', source: '', message: '' });
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-white rounded-2xl border-[#E5E2DC]" data-testid="create-enquiry-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">Add Enquiry</DialogTitle>
          <DialogDescription className="text-xs text-[#8A8B85]">Log a new enquiry quickly</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-1">
          <div>
            <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Name</Label>
            <Input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Full name" className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-10" data-testid="enq-name-input" autoFocus />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Phone</Label>
              <Input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="07700 900000" className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-10" data-testid="enq-phone-input" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Email</Label>
              <Input value={form.email} onChange={e => set('email', e.target.value)} placeholder="email@example.com" className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-10" data-testid="enq-email-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Service Type</Label>
              <Input value={form.business_type} onChange={e => set('business_type', e.target.value)} placeholder="e.g. Massage, Beauty" className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-10" data-testid="enq-service-input" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Source</Label>
              <Select value={form.source} onValueChange={v => set('source', v)}>
                <SelectTrigger className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-10" data-testid="enq-source-select">
                  <SelectValue placeholder="Select source" />
                </SelectTrigger>
                <SelectContent>
                  {['WhatsApp', 'Walk-in', 'Referral', 'Website', 'Phone', 'Email', 'Social Media', 'Other'].map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Preferred Location</Label>
            <Input value={form.preferred_location} onChange={e => set('preferred_location', e.target.value)} placeholder="e.g. Manchester, ground floor" className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-10" data-testid="enq-location-input" />
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Notes (optional)</Label>
            <Textarea value={form.message} onChange={e => set('message', e.target.value)} placeholder="Any initial notes..." rows={2} className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl resize-none" data-testid="enq-notes-input" />
          </div>
          <Button onClick={handleSave} disabled={saving || !form.name || (!form.email && !form.phone)}
            className="w-full bg-[#3A4035] text-white rounded-full h-10 hover:bg-[#2B3027]" data-testid="save-enquiry-button">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />}Add Enquiry
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ── Onboarding Modal (2-step) ──
function OnboardingModal({ open, onClose, enq, onDone }) {
  const reservation = enq?.reservation;
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ full_name: enq?.name || '', email: enq?.email || '', phone: enq?.phone || '', home_address: '' });
  const [saving, setSaving] = useState(false);
  const [idFile, setIdFile] = useState(null);
  const [proofFile, setProofFile] = useState(null);
  const [uploadedId, setUploadedId] = useState(false);
  const [uploadedProof, setUploadedProof] = useState(false);
  const [completing, setCompleting] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const resId = reservation?.reservation_id;

  const handleSavePersonal = async () => {
    if (!form.full_name || !form.email || !form.phone || !form.home_address) return;
    setSaving(true);
    try { await savePersonalDetails(resId, form); setStep(2); }
    catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const handleUploadId = async () => {
    if (!idFile) return;
    setSaving(true);
    try { await uploadOnboardingDoc(resId, idFile, 'photo_id'); setUploadedId(true); }
    catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const handleUploadProof = async () => {
    if (!proofFile) return;
    setSaving(true);
    try { await uploadOnboardingDoc(resId, proofFile, 'proof_of_address'); setUploadedProof(true); }
    catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const handleComplete = async () => {
    setCompleting(true);
    try { await completeOnboarding(resId); onDone(); onClose(); }
    catch (err) { console.error(err); alert(err.response?.data?.detail || 'Please upload both documents'); }
    finally { setCompleting(false); }
  };

  if (!reservation) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg bg-white rounded-2xl border-[#E5E2DC]" data-testid="onboarding-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">Onboarding — {enq.name}</DialogTitle>
          <DialogDescription className="text-xs text-[#8A8B85]">Step {step} of 2 — {step === 1 ? 'Personal Details' : 'Document Uploads'}</DialogDescription>
        </DialogHeader>

        {/* Step indicators */}
        <div className="flex items-center gap-3 pt-1 pb-3">
          <div className={`flex items-center gap-2 ${step >= 1 ? 'text-[#3A4035]' : 'text-[#8A8B85]'}`}>
            <span className={`w-7 h-7 rounded-full text-xs font-semibold flex items-center justify-center ${step > 1 ? 'bg-[#E8EDE5] text-[#3A4035]' : step === 1 ? 'bg-[#3A4035] text-white' : 'bg-[#F2F0EB]'}`}>{step > 1 ? <Check className="w-3.5 h-3.5" strokeWidth={2}/> : '1'}</span>
            <span className="text-xs font-medium">Personal</span>
          </div>
          <div className="flex-1 h-px bg-[#E5E2DC]" />
          <div className={`flex items-center gap-2 ${step >= 2 ? 'text-[#3A4035]' : 'text-[#8A8B85]'}`}>
            <span className={`w-7 h-7 rounded-full text-xs font-semibold flex items-center justify-center ${step === 2 ? 'bg-[#3A4035] text-white' : 'bg-[#F2F0EB]'}`}>2</span>
            <span className="text-xs font-medium">Documents</span>
          </div>
        </div>

        {step === 1 && (
          <div className="space-y-4">
            <div>
              <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Full Name</Label>
              <Input value={form.full_name} onChange={e => set('full_name', e.target.value)} className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-10" data-testid="onb-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Email</Label>
                <Input value={form.email} onChange={e => set('email', e.target.value)} className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-10" data-testid="onb-email" />
              </div>
              <div>
                <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Phone</Label>
                <Input value={form.phone} onChange={e => set('phone', e.target.value)} className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-10" data-testid="onb-phone" />
              </div>
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Home Address</Label>
              <Textarea value={form.home_address} onChange={e => set('home_address', e.target.value)} rows={2} className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl resize-none" data-testid="onb-address" />
            </div>
            <Button onClick={handleSavePersonal} disabled={saving || !form.full_name || !form.email || !form.phone || !form.home_address}
              className="w-full bg-[#3A4035] text-white rounded-full h-10 hover:bg-[#2B3027]" data-testid="onb-next-step">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Next: Documents
            </Button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5">
            {/* Photo ID */}
            <div>
              <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55] mb-1 block">Photo ID <span className="text-[#B33A3A]">*</span></Label>
              <p className="text-[10px] text-[#8A8B85] mb-2">Please upload a valid photo ID (passport or driving licence)</p>
              {uploadedId ? (
                <div className="flex items-center gap-2 p-3 bg-[#E8EDE5] rounded-xl"><Check className="w-4 h-4 text-[#3A4035]" strokeWidth={1.5}/><span className="text-xs font-medium text-[#3A4035]">{idFile?.name || 'Uploaded'}</span></div>
              ) : (
                <div className="flex items-center gap-2">
                  <input type="file" accept="image/*,.pdf" onChange={e => setIdFile(e.target.files[0])} className="text-xs flex-1" data-testid="onb-id-file" />
                  <Button onClick={handleUploadId} disabled={!idFile || saving} size="sm" className="bg-[#3A4035] text-white rounded-full px-4 hover:bg-[#2B3027] text-xs" data-testid="onb-upload-id">
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Upload'}
                  </Button>
                </div>
              )}
            </div>
            {/* Proof of address */}
            <div>
              <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55] mb-1 block">Proof of Address <span className="text-[#B33A3A]">*</span></Label>
              <p className="text-[10px] text-[#8A8B85] mb-2">Please upload a utility bill or bank statement dated within the last 3 months</p>
              {uploadedProof ? (
                <div className="flex items-center gap-2 p-3 bg-[#E8EDE5] rounded-xl"><Check className="w-4 h-4 text-[#3A4035]" strokeWidth={1.5}/><span className="text-xs font-medium text-[#3A4035]">{proofFile?.name || 'Uploaded'}</span></div>
              ) : (
                <div className="flex items-center gap-2">
                  <input type="file" accept="image/*,.pdf" onChange={e => setProofFile(e.target.files[0])} className="text-xs flex-1" data-testid="onb-proof-file" />
                  <Button onClick={handleUploadProof} disabled={!proofFile || saving} size="sm" className="bg-[#3A4035] text-white rounded-full px-4 hover:bg-[#2B3027] text-xs" data-testid="onb-upload-proof">
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Upload'}
                  </Button>
                </div>
              )}
            </div>
            {/* Validation message */}
            {(!uploadedId || !uploadedProof) && (
              <p className="text-xs text-[#8A7230] bg-[#FDFAF0] rounded-xl p-3 border border-[#EDE4C8]" data-testid="doc-validation-msg">
                Both documents are required before you can submit the application.
              </p>
            )}
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1 rounded-full h-10 border-[#E5E2DC]">Back</Button>
              <Button onClick={handleComplete} disabled={completing || !uploadedId || !uploadedProof}
                className="flex-1 bg-[#3A4035] text-white rounded-full h-10 hover:bg-[#2B3027]" data-testid="onb-complete">
                {completing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" strokeWidth={1.5}/>}Submit Application
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


// ── Review Onboarding Modal ──
function ReviewOnboardingModal({ open, onClose, enq, onDone }) {
  const reservation = enq?.reservation;
  const onboarding = enq?.onboarding;
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const pd = onboarding?.personal_details || {};
  const docs = onboarding?.documents || [];

  const handleDecision = async (decision) => {
    setSaving(true);
    try {
      await reviewOnboarding(reservation.reservation_id, decision, notes);
      onDone();
      onClose();
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  if (!reservation || !onboarding) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg bg-white rounded-2xl border-[#E5E2DC]" data-testid="review-onboarding-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">Review Onboarding — {enq.name}</DialogTitle>
          <DialogDescription className="text-xs text-[#8A8B85]">Review applicant details and documents</DialogDescription>
        </DialogHeader>

        {/* Personal details summary */}
        <div className="bg-[#FAF9F6] rounded-xl p-4 border border-[#F2F0EB] space-y-2">
          <p className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#8A8B85]">Personal Details</p>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div><span className="text-[#8A8B85]">Name:</span> <span className="font-medium text-[#1A1A1A]">{pd.full_name}</span></div>
            <div><span className="text-[#8A8B85]">Email:</span> <span className="font-medium text-[#1A1A1A]">{pd.email}</span></div>
            <div><span className="text-[#8A8B85]">Phone:</span> <span className="font-medium text-[#1A1A1A]">{pd.phone}</span></div>
            <div><span className="text-[#8A8B85]">Address:</span> <span className="font-medium text-[#1A1A1A]">{pd.home_address}</span></div>
          </div>
        </div>

        {/* Documents summary */}
        <div className="bg-[#FAF9F6] rounded-xl p-4 border border-[#F2F0EB]">
          <p className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#8A8B85] mb-2">Documents ({docs.length})</p>
          <div className="space-y-1.5">
            {docs.map(d => (
              <div key={d.doc_id} className="flex items-center gap-2 text-xs">
                <Check className="w-3.5 h-3.5 text-[#3A4035]" strokeWidth={1.5} />
                <span className="font-medium text-[#1A1A1A] capitalize">{d.doc_type.replace(/_/g, ' ')}</span>
                <span className="text-[#8A8B85]">— {d.original_filename}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Review notes */}
        <div>
          <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Review Notes (optional)</Label>
          <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any notes for this review..." rows={2}
            className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl resize-none text-xs" data-testid="review-notes" />
        </div>

        {/* Decision buttons */}
        <div className="flex gap-3 pt-1">
          <button onClick={() => handleDecision('needs_follow_up')} disabled={saving}
            className="flex-1 flex items-center justify-center gap-2 p-3 rounded-2xl border border-[#F0D4D4] bg-[#FDF2F2] text-[#B33A3A] hover:bg-[#F8E6E6] transition-colors text-sm font-medium"
            data-testid="review-needs-followup">
            <AlertCircle className="w-4 h-4" strokeWidth={1.5} />Needs Follow-up
          </button>
          <button onClick={() => handleDecision('approved')} disabled={saving}
            className="flex-1 flex items-center justify-center gap-2 p-3 rounded-2xl border border-[#D0D9CB] bg-[#E8EDE5] text-[#3A4035] hover:bg-[#D8E2D3] transition-colors text-sm font-medium"
            data-testid="review-approve">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" strokeWidth={1.5} />}Approve
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
